import { createResource, createSignal, on, onCleanup, onMount, Show } from "solid-js";

import { ProjectView } from "./Project";

import Admin, { AdminMenu } from "./Admin";

import socket, { SocketStatus } from "./socket";
import { useUserContext } from "./UserContext";
import { Loader, NavDropdown } from "./Components";
import UserSettings from "./UserSettings";
import Conventions from "./Conventions";
import PromoSelector from "./PromoSelector";
import TeacherView from "./TeacherView";
import { useI18n } from "./i18n";

// Fullname Form Component
const FullnameForm = () => {
  const t = useI18n();
  const [userStore, setUserStore, userActions] = useUserContext();
  const [fullname, setFullname] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal("");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fullname().trim()) {
      setError(t("Please enter your full name"));
      return;
    }

    setSaving(true);
    setError("");

    userActions.updateUserProfile({ fullname: fullname() })
      .then(() => {
        setSaving(false);
      })
      .catch(err => {
        setError(t("Error updating profile. Please try again."));
        setSaving(false);
      });
  };

  return (
    <form onSubmit={handleSubmit} class="mt-3">
      <div class="row g-3 align-items-center">
        <div class="col-auto">
          <input
            type="text"
            class="form-control"
            value={fullname()}
            onInput={(e) => setFullname(e.target.value)}
            placeholder={t("Enter your full name")}
            required
          />
        </div>
        <div class="col-auto">
          <button
            type="submit"
            class="btn btn-primary"
            disabled={saving()}
          >
            {saving() ? t("Saving...") : t("Save")}
          </button>
        </div>
      </div>
      {error() && (
        <div class="text-danger mt-2">{error()}</div>
      )}
    </form>
  );
};

export default function App() {

  const t = useI18n();
  const [userStore, setUserStore, userActions] = useUserContext();
  const [navbarExpanded, setNavbarExpanded] = createSignal(false); // Add this line
  const [log, setLog] = createSignal('');

  onMount(() => {
    socket.on('log', (data) => {
      setLog(log() + '\n' + data);
    });
  });

  // Add this function to toggle navbar state
  const toggleNavbar = () => {
    setNavbarExpanded(!navbarExpanded());
  };

  return (
    <>
      <div class="container">
        <nav class="navbar navbar-expand-lg sticky-top navbar-light bg-light">
          <div class="container-fluid">
            <a
              onclick={(e) => { e.preventDefault(); userActions.setPage('/'); }}
              class="navbar-brand" href="/">{t("Pre-Stage")}</a>
            <button 
              class="navbar-toggler" 
              type="button" 
              onClick={toggleNavbar} // Add this onClick handler
              aria-controls="navbarNavDropdown" 
              aria-expanded={navbarExpanded() ? "true" : "false"}
              aria-label="Toggle navigation"
            >
              <span class="navbar-toggler-icon"></span>
            </button>
            <div class={`collapse navbar-collapse ${navbarExpanded() ? 'show' : ''}`} id="navbarNavDropdown">
              <ul class="navbar-nav">
                <li class="nav-item">
                  <a
                    onclick={(e) => { e.preventDefault(); userActions.setPage('/'); }}
                    class="nav-link active"
                    aria-current="page" href="/">{t("Home")}</a>
                </li>
                <NavDropdown label={
                  userStore.username ?
                    (userStore.fullname ?
                      <span>{userStore.fullname} <small class="text-muted">({userStore.username})</small></span> :
                      userStore.username
                    ) :
                    'Non connecté'
                }>
                  <li><a
                    onclick={(e) => { e.preventDefault(); userActions.setPage('/user-settings'); }}
                    class="dropdown-item" href="/user-settings">{t("Settings")}</a></li>
                </NavDropdown>
                <Show when={userStore.role === 'admin'}>
                  <AdminMenu />
                </Show>
              </ul>
            </div>
          </div>
        </nav>
        <Show when={!userStore.username && userStore.loadingUser}>
          <Loader />
        </Show>
        <Show when={!userStore.username && !userStore.loadingUser}>
          <h1>Non connecté</h1>
          <button type="button" class="btn btn-primary"
            onClick={() => {
              location.href = '/login';
            }}>
            Se connecter
          </button>
        </Show>

        <Show when={userStore.username}>
          <Show when={userStore.role === 'admin' && (
            userStore.page === '/' ||
            userStore.page === '/manage-users' ||
            userStore.page === '/manage-promos' ||
            userStore.page === '/manage-notifications' ||
            userStore.page === '/manage-years' ||
            userStore.page === '/manage-convention-structure' ||
            userStore.page === '/manage-email-templates'
          )}>
            <Admin />
          </Show>
          <Show when={userStore.role === 'teacher' && userStore.page === '/'}>
            <TeacherView />
          </Show>
          {/* Student home page - only shown when on homepage */}
          <Show when={userStore.role !== 'admin' && userStore.role !== 'teacher' && userStore.page === '/'}>
            <Show
              when={userStore.promo_id}
              fallback={<PromoSelector />}
            >
              <Show when={
                userStore.fullname && 
                userStore.student_number && 
                userStore.additional_info?.healthInsurance
              } fallback={
                <div class="alert alert-warning mb-4">
                  <h4 class="alert-heading">{t("Complete Your Profile")}</h4>
                  <p>{t("Please complete your profile information before creating internship agreements:")}</p>
                  <ul>
                    <Show when={!userStore.fullname}>
                      <li>{t("Full Name")}</li>
                    </Show>
                    <Show when={!userStore.student_number}>
                      <li>{t("Student Number")}</li>
                    </Show>
                    <Show when={!userStore.additional_info?.healthInsurance}>
                      <li>{t("Health Insurance - City + Region")}</li>
                    </Show>
                  </ul>
                  <p>{t("Please also verify that your email address is correct.")}</p>
                  <button 
                    type="button" 
                    class="btn btn-primary"
                    onClick={() => userActions.setPage('/user-settings')}
                  >
                    {t("Go to Settings")}
                  </button>
                </div>
              }>
                <h1 class="mb-4">{t("Internship Management Platform")}</h1>
                <Conventions />
              </Show>
            </Show>
          </Show>

          {/* Settings page - shown for all user types */}
          <Show when={userStore.page === '/user-settings'}>
            <UserSettings />
          </Show>

          <br />
          <pre>
            {log()}
          </pre>
        </Show>
      </div>
      <SocketStatus />
    </>
  );
}
